import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Zap, Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    if (formData.password.length < 6) {
      setError('La password deve avere almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      // Register as super_admin (first user of the agency)
      await axios.post(`${API_URL}/api/auth/register`, {
        nome: formData.nome,
        cognome: formData.cognome,
        email: formData.email,
        telefono: formData.telefono,
        password: formData.password,
        ruolo: 'super_admin',
        attivo: true
      });

      // Auto login after registration
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError('Registrazione completata. Effettua il login.');
        navigate('/login');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante la registrazione');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Background image */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1772033762692-22922c5c8a79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmUlMjBicmlnaHR8ZW58MHx8fHwxNzcyODA1OTAxfDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-sky-600">
              <Zap className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">F.lli Schiliro' Servizi</h1>
              <p className="text-sm text-slate-300">Gestione Luce & Gas</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <blockquote className="text-2xl font-light leading-relaxed">
              "Inizia subito a gestire la tua agenzia energia in modo professionale."
            </blockquote>
            <div className="space-y-2">
              <p className="text-slate-300">Con F.lli Schiliro' Servizi potrai:</p>
              <ul className="space-y-1 text-slate-400">
                <li>✓ Gestire lead e clienti</li>
                <li>✓ Monitorare pratiche luce e gas</li>
                <li>✓ Creare sub-agenti con aree geografiche</li>
                <li>✓ Tracciare commissioni e performance</li>
              </ul>
            </div>
          </div>

          <div className="text-sm text-slate-400">
            Registrazione gratuita • Nessuna carta richiesta
          </div>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-sky-600">
              <Zap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">F.lli Schiliro' Servizi</h1>
              <p className="text-sm text-slate-500">Gestione Luce & Gas</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-slate-900">Registrati</CardTitle>
              <CardDescription className="text-slate-500">
                Crea il tuo account Admin per gestire la tua agenzia
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertDescription className="text-red-700">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome" className="text-slate-700">Nome *</Label>
                    <Input
                      id="nome"
                      name="nome"
                      type="text"
                      placeholder="Mario"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      className="h-11"
                      data-testid="register-nome-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cognome" className="text-slate-700">Cognome *</Label>
                    <Input
                      id="cognome"
                      name="cognome"
                      type="text"
                      placeholder="Rossi"
                      value={formData.cognome}
                      onChange={handleChange}
                      required
                      className="h-11"
                      data-testid="register-cognome-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="mario.rossi@agenzia.it"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-11"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefono" className="text-slate-700">Telefono</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    placeholder="+39 333 1234567"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="h-11"
                    data-testid="register-telefono-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Password *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimo 6 caratteri"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="h-11 pr-10"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Conferma Password *</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Ripeti la password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="h-11"
                    data-testid="register-confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Registrazione in corso...
                    </>
                  ) : (
                    'Crea Account Admin'
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-200 text-center">
                <p className="text-sm text-slate-500">
                  Hai già un account?{' '}
                  <Link to="/login" className="text-sky-600 hover:text-sky-700 font-medium">
                    Accedi
                  </Link>
                </p>
              </div>

              <div className="mt-4 p-4 bg-sky-50 rounded-lg border border-sky-100">
                <p className="text-xs text-sky-700">
                  <strong>Nota:</strong> Registrandoti crei un account Super Admin. 
                  Potrai poi creare sub-agenti e assegnare loro aree geografiche dal pannello Utenti.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Torna al login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
